import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  QUERY_ENDED_PARKING_EVENTS,
  SHORT_CACHE_TIME_MS,
} from '../../apis/moovyQueryKeys'
import {
  ParkingEventDTO,
  ParkingEventsSortType,
  PaymentStatus,
  paymentStatuses,
} from '../../apis/parkingEvents/dto'
import {
  MoovyGrid,
  MoovyGridCard,
  MoovyScrollToTop,
  MoovySortControl,
  MoovyTable,
  MoovyTableControlLayout,
  PrettyCurrency,
} from '../../components'
import {
  MoovyPaginationProps,
  PaginationAndSortingValues,
} from '../../components/moovyPagination'
import MoovySearch, {
  DateTimeRangeStringValues,
} from '../../components/moovySearch'
import { prettyDuration, prettyFullTimestamp } from '../../utils/datetimeUtils'
import PaymentStatusIcon, {
  PaymentStatusIconWithText,
} from './paymentStatusIcon'
import WhitelistedVehicleIcon, {
  WhitelistedVehicleIconWithLicensePlate,
} from './whitelistedVehicleIcon'

import MenuItem from '@mui/material/MenuItem'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDefaultPaginationAndSortingValues } from '../../apis'
import { ParkingEventsApi } from '../../apis/parkingEvents/parkingEventsApi'
import { appRoutes } from '../../app/permissions'
import { getSortItems } from '../../components/moovySortControl/sortValues'

const EndedParkingEvents = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const defaultPaymentStatuses = Object.values(paymentStatuses).filter(
    (paymentStatus) => paymentStatus !== paymentStatuses.TIMED_OUT,
  )

  const defaultSortCriteria: ParkingEventsSortType = 'END'
  const [paginationAndSortingValues, setPaginationAndSortingValues] =
    useState<PaginationAndSortingValues>({
      ...getDefaultPaginationAndSortingValues(defaultSortCriteria),
    })

  type EndedParkingEventsSearchParameters = {
    licensePlate: string
    start: string | null
    end: string | null
    lane: string
    paymentStatus: PaymentStatus[]
  }

  const [searchParameters, setSearchParameters] =
    useState<EndedParkingEventsSearchParameters>({
      licensePlate: '',
      start: null,
      end: null,
      lane: '',
      paymentStatus: defaultPaymentStatuses,
    })

  const queryKey = [
    QUERY_ENDED_PARKING_EVENTS,
    searchParameters.licensePlate,
    searchParameters.start,
    searchParameters.end,
    searchParameters.lane,
    searchParameters.paymentStatus,
    paginationAndSortingValues.page,
    paginationAndSortingValues.sort,
    paginationAndSortingValues.direction,
  ]

  const queryEndedParkingEvents = useQuery({
    queryKey,
    queryFn: () =>
      ParkingEventsApi.getEndedParkingEvents({
        paginationAndSorting: paginationAndSortingValues,
        searchParameters: {
          ...searchParameters,
          paymentStatus: searchParameters.paymentStatus,
        },
      }),
    staleTime: SHORT_CACHE_TIME_MS,
  })

  const pagination: MoovyPaginationProps = {
    page: paginationAndSortingValues.page,
    limit: paginationAndSortingValues.limit,
    total: queryEndedParkingEvents.data?.data.total ?? 0,
    onNextPage: (page) => {
      setPaginationAndSortingValues({
        ...paginationAndSortingValues,
        page: page,
      })
    },
  }

  const [licensePlateInput, setLicensePlateInput] = useState(
    searchParameters.licensePlate,
  )
  const [dateTimeRangeInput, setDateTimeRangeInput] =
    useState<DateTimeRangeStringValues>({
      start: searchParameters.start,
      end: searchParameters.end,
    })
  const [laneInput, setLaneInput] = useState(searchParameters.lane)
  const [paymentStatusFilterInput, setPaymentStatusFilterInput] = useState<
    PaymentStatus[]
  >(defaultPaymentStatuses)

  const onSearch = () => {
    setPaginationAndSortingValues({
      ...paginationAndSortingValues,
      page: 1,
    })
    setSearchParameters({
      licensePlate: licensePlateInput,
      start: dateTimeRangeInput.start,
      end: dateTimeRangeInput.end,
      lane: laneInput,
      paymentStatus: paymentStatusFilterInput.filter(
        (status) => paymentStatuses[status],
      ) as PaymentStatus[],
    })
    // Override cache if search parameters have not been changed
    if (
      searchParameters.licensePlate === licensePlateInput &&
      searchParameters.start === dateTimeRangeInput.start &&
      searchParameters.end === dateTimeRangeInput.end &&
      searchParameters.lane === laneInput &&
      JSON.stringify(searchParameters.paymentStatus) ===
        JSON.stringify(paymentStatusFilterInput)
    ) {
      queryClient.invalidateQueries({ queryKey })
    }
  }

  const onClickRow = (parkingEvent: ParkingEventDTO) => {
    navigate(`${appRoutes.PARKING_EVENTS}/${parkingEvent.id}`)
  }

  const parkingEvents: ParkingEventDTO[] =
    queryEndedParkingEvents.data?.data.data || []
  return (
    <>
      <MoovyScrollToTop />
      <MoovyTableControlLayout
        searchControl={
          <MoovySearch.SearchBar onSearch={onSearch} breakpoint="lg">
            <MoovySearch.TextSearchField
              label={intl.formatMessage({ id: 'common.text.licensePlate' })}
              stateControl={[licensePlateInput, setLicensePlateInput]}
            />
            <MoovySearch.DateTimeRangePickerSearchField
              stateControl={[dateTimeRangeInput, setDateTimeRangeInput]}
            />
            <MoovySearch.TextSearchField
              label={intl.formatMessage({ id: 'common.text.entryLane' })}
              stateControl={[laneInput, setLaneInput]}
            />
            <MoovySearch.SelectSearchField
              label={intl.formatMessage({ id: 'common.text.paymentStatus' })}
              stateControl={[
                paymentStatusFilterInput,
                setPaymentStatusFilterInput,
              ]}
            >
              {Object.values(paymentStatuses).map((item) => (
                <MenuItem key={item} value={item}>
                  <PaymentStatusIconWithText paymentStatus={item} />
                </MenuItem>
              ))}
            </MoovySearch.SelectSearchField>
          </MoovySearch.SearchBar>
        }
        sortControl={
          <MoovySortControl
            sortOptions={getSortItems(intl, 'PARKING_EVENTS_ENDED')}
            defaultSortCriteria={defaultSortCriteria}
            paginationAndSortingValues={paginationAndSortingValues}
            setPaginationAndSortingValues={setPaginationAndSortingValues}
          />
        }
      />

      <MoovyGrid
        emptyTextId="page.parkingEvents.ended.emptyText"
        errorTextId="common.actions.get.error"
        query={queryEndedParkingEvents}
        pagination={pagination}
      >
        {parkingEvents.map((parkingEvent) => (
          <MoovyGridCard
            key={parkingEvent.id}
            cardTitle={
              <Typography fontWeight={600}>
                {parkingEvent.parkingType === 'WHITELIST' ? (
                  <WhitelistedVehicleIcon />
                ) : null}
                {parkingEvent.licensePlate}
              </Typography>
            }
            cardSecondaryTitle={
              <Typography>
                <FormattedMessage id="common.text.duration" />:{' '}
                {prettyDuration(intl, parkingEvent)}
              </Typography>
            }
            avatarName={
              parkingEvent.parkingType === 'WHITELIST'
                ? intl.formatMessage({ id: 'common.text.whitelist' })[0]
                : ''
            }
            cardContentList={[
              <Typography>
                <FormattedMessage id="common.text.startTime" />:{' '}
                {prettyFullTimestamp(intl, parkingEvent.startTime)}
              </Typography>,
              <Typography>
                <FormattedMessage id="common.text.endTime" />:{' '}
                {prettyFullTimestamp(intl, parkingEvent.endTime)}
              </Typography>,
              <Typography>
                <FormattedMessage id="common.text.price" />
                : <PrettyCurrency cents={parkingEvent.price} />
              </Typography>,
              <PaymentStatusIconWithText
                paymentStatus={parkingEvent.paymentStatus}
              />,
            ]}
            onCardClicked={() => onClickRow(parkingEvent)}
          />
        ))}
      </MoovyGrid>
      <MoovyTable
        items={parkingEvents}
        query={queryEndedParkingEvents}
        pagination={pagination}
        emptyTextId="page.parkingEvents.ended.emptyText"
        errorTextId="common.actions.get.error"
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <FormattedMessage id="common.text.licensePlate" />
            </TableCell>
            <TableCell>
              <FormattedMessage id="common.text.startTime" />
            </TableCell>
            <TableCell>
              <FormattedMessage id="common.text.endTime" />
            </TableCell>
            <TableCell align="right">
              <FormattedMessage id="common.text.duration" />
            </TableCell>
            <TableCell align="right">
              <FormattedMessage id="common.text.price" />
            </TableCell>
            <TableCell align="right">
              <FormattedMessage id="common.text.paymentStatus" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {parkingEvents.map((parkingEvent) => (
            <TableRow
              key={parkingEvent.id}
              onClick={() => onClickRow(parkingEvent)}
              hover
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                <WhitelistedVehicleIconWithLicensePlate
                  parkingEvent={parkingEvent}
                />
              </TableCell>
              <TableCell>
                <Typography>
                  {prettyFullTimestamp(intl, parkingEvent.startTime)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography>
                  {prettyFullTimestamp(intl, parkingEvent.endTime)}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>{prettyDuration(intl, parkingEvent)}</Typography>
              </TableCell>
              <TableCell align="right">
                <PrettyCurrency cents={parkingEvent.price} />
              </TableCell>
              <TableCell align="right">
                <PaymentStatusIcon paymentStatus={parkingEvent.paymentStatus} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </MoovyTable>
    </>
  )
}

export default EndedParkingEvents
